/**
 * Cabeçalho fixo do topo de toda tela de módulo.
 *
 * Papel: desenha o título da página como `h1` serifado, um subtítulo mono
 * opcional logo abaixo dele, e a ação de voltar à direita — é o primeiro
 * conteúdo de cada `pages/pagina-*.tsx`.
 *
 * Como usar: `titulo` é o único texto que todo leitor de tela ouve como
 * cabeçalho de nível 1 daquela página, e por isso nenhum `h1` deverá se
 * repetir mais abaixo dela. `voltarPara` tem `/` como padrão, e só precisa
 * ser passado quando o retorno correto é outro endereço — o submenu de onde a
 * tela foi aberta, por exemplo. `acoes` recebe o que precisar ficar entre o
 * título e o botão de voltar; sem nada para pôr ali, a prop fica de fora da
 * chamada e só o botão aparece à direita.
 *
 * O texto de cada subtítulo, e de onde ele veio, fica registrado em
 * `docs/identidade-visual.md` — não aqui.
 *
 * `acoes` e o botão de voltar somem juntos na impressão (`print:hidden`): os
 * dois são controle de navegação, não conteúdo, e nenhuma tela que manda
 * imprimir quer os dois por cima do que a pessoa pediu para sair no papel.
 * `titulo` e `subtitulo` continuam visíveis — são o que identifica o que saiu
 * impresso.
 */
import { type ReactNode } from 'react';
import { Link } from 'react-router';

import { Button } from '@/shared/ui/button';

interface CabecalhoDoModuloProps {
  /** Título da página, desenhado como o único `h1` dela. */
  titulo: string;
  /** Linha mono abaixo do título; ausente quando a tela não tem uma. */
  subtitulo?: string;
  /** Destino do botão de voltar. @default '/' */
  voltarPara?: string;
  /** Conteúdo entre o título e o botão de voltar, como um botão de ação da tela. */
  acoes?: ReactNode;
}

/**
 * O cabeçalho de uma tela de módulo.
 *
 * @param titulo - Título da página, desenhado como o único `h1` dela.
 * @param subtitulo - Linha mono abaixo do título; ausente quando a tela não
 *   tem uma.
 * @param voltarPara - Destino do botão de voltar; sem valor, leva à tela
 *   inicial.
 * @param acoes - Conteúdo entre o título e o botão de voltar.
 * @returns O bloco do título com o subtítulo, e a ação de voltar à direita.
 */
export function CabecalhoDoModulo({
  titulo,
  subtitulo,
  voltarPara = '/',
  acoes,
}: CabecalhoDoModuloProps) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-serif text-[28px] font-normal text-foreground">{titulo}</h1>
        {subtitulo !== undefined && (
          <span className="mt-1.5 block font-mono text-[11px] tracking-[0.08em] text-texto-fraco">
            {subtitulo}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        {acoes}
        <Button variant="outline" asChild>
          <Link to={voltarPara}>← Voltar</Link>
        </Button>
      </div>
    </div>
  );
}
